import { ComponentProps, ElementRef, forwardRef } from 'react';

import cn from 'classnames';
import { BsInfoCircle } from 'react-icons/bs';
import CurrencyInput from 'react-currency-input-field';
import Tooltip from 'ui/components/common/Tooltip';
import { SIZE } from 'utils/constants';
import { cva } from 'class-variance-authority';

import { inputClasses } from '../Input/Input';

type PROPS = Omit<ComponentProps<typeof CurrencyInput>, 'size'> & {
  size?: SIZE;
  textType?: 'default' | 'danger' | 'success' | 'warning';
  name?: string;
  label?: string;
  labelSubText?: string;
  subText?: string;
  required?: boolean;
  information?: string;
  hideNumberStepper?: boolean;
  hidePrefix?: boolean;
  prefix?: string;
  suffix?: string;
};

const subtext = cva(['leading-4 text-content'], {
  variants: {
    textType: {
      default: [],
      danger: ['!text-danger-base'],
      warning: ['!text-warning-base'],
      success: ['!text-success-base'],
    },
    size: {
      [SIZE.Small]: 'text-sm',
      [SIZE.Medium]: 'text-sm',
      [SIZE.Large]: 'text-sm',
    },
  },
});

const Currency = forwardRef<ElementRef<'input'>, PROPS>(
  (
    {
      className,
      size = SIZE.Small,
      textType,
      required = false,
      label,
      labelSubText = '(Optional)',
      subText,
      name,
      information,
      prefix = '$',
      suffix,
      hidePrefix,
      ...props
    },
    ref,
  ) => (
    <div className={cn('flex flex-col max gap-y-2', className)}>
      {(label || information) && (
        <div className="flex items-center justify-between">
          {label && (
            <label htmlFor={name}>
              <span className="mr-1 text-sm font-semibold">{label}</span>
              {required ? (
                <span className="text-danger-base">*</span>
              ) : (
                <span className="text-xs text-content">{labelSubText}</span>
              )}
            </label>
          )}
          {!!information && (
            <Tooltip placement="right" title={information}>
              <div className="text-secondary-base">
                <BsInfoCircle />
              </div>
            </Tooltip>
          )}
        </div>
      )}

      <CurrencyInput
        className={cn(inputClasses({ size, textType }))}
        ref={ref}
        prefix={!hidePrefix ? prefix : ''}
        suffix={suffix}
        decimalsLimit={2}
        {...props}
      />

      {subText ? (
        <div className={cn(subtext({ size, textType }))}>{subText}</div>
      ) : null}
    </div>
  ),
);

export default Currency;
